import React, { useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { api } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import { optionsStatusProps, useOptions } from '../hooks/useOptions';
import { usePolicyList, useSubmit } from '../hooks/usePolicyList';
import { useActionDelete } from '../hooks/useActionDelete';
import { ResultViewer } from '../components/ResultViewer';
import { PolicyModal } from '../components/PolicyModal';
import { UserFields } from '../components/UserFields';
import { deleteColumn } from '../components/deleteColumn';
import { ActionChecklist } from '../components/ActionChecklist';
import {
  USER_RESOURCE_ACTION_COLUMNS,
  UserResourceActionRow,
  resourceOptions,
  toNamedOptions,
  toResourceChoices,
  toUserResourceActionRows,
} from './policyRows';

export const UserResourceActionPage: React.FC<{ app: string }> = ({ app }) => {
  const { base, loading, result, reload } = usePolicyList(app, 'user_resource_action');
  const { addNotification } = useNotifications();
  const rows = useMemo(() => toUserResourceActionRows(result?.data), [result]);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [userType, setUserType] = useState('PERSON');
  const [user, setUser] = useState('');
  const [resourcePath, setResourcePath] = useState('');
  const [actions, setActions] = useState<readonly MultiselectProps.Option[]>([]);
  const resources = useOptions(`/${encodeURIComponent(app)}/resources`, toResourceChoices);
  const actionList = useOptions(`/${encodeURIComponent(app)}/actions`, toNamedOptions);
  const create = useSubmit();

  // Delete confirmation
  const del = useActionDelete<UserResourceActionRow>(
    (r, name) => api('DELETE', base, { user: { type: r.userType, value: r.user }, resource: r.resource, action: { name } }),
    async (r, removed) => {
      addNotification('success', `Removed ${removed.join(', ')} on ${r.resourcePath} from ${r.userType} ${r.user}`);
      await reload();
    }
  );

  const columns = useMemo(
    () => [
      ...USER_RESOURCE_ACTION_COLUMNS,
      deleteColumn<UserResourceActionRow>(
        r => (r.userType && r.user && r.resource && r.actions.length ? `${r.userType} ${r.user} on ${r.resourcePath}` : null),
        r => del.open(r)
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open only calls state setters
    []
  );

  if (!app) return <Alert type="info">Select an application from the top bar.</Alert>;

  const openCreate = () => {
    setUserType('PERSON');
    setUser('');
    setResourcePath('');
    setActions([]);
    create.setError(null);
    setCreateOpen(true);
    resources.load();
    actionList.load();
  };

  const confirmCreate = async () => {
    const value = user.trim();
    const resource = resources.state.items.find(c => c.path === resourcePath)?.resource;
    const names = actions.map(a => a.value!);
    const ok = await create.submit(() =>
      api('PUT', base, {
        users: [{ type: userType, value }],
        resources_with_actions: [{ resource, actions: names.map(name => ({ name })) }],
      })
    );
    if (ok) {
      setCreateOpen(false);
      addNotification('success', `Granted ${names.join(', ')} on ${resourcePath} to ${userType} ${value}`);
      await reload();
    }
  };

  const selectedResource = resourcePath ? { value: resourcePath, label: resourcePath } : null;
  const canCreate = user.trim() !== '' && resourcePath !== '' && actions.length > 0;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <Button variant="primary" onClick={openCreate}>
            Create Policy
          </Button>
        }
      >
        User - Resource - Action Policy
      </Header>

      {loading && !result ? (
        <StatusIndicator type="loading">Loading policies</StatusIndicator>
      ) : (
        <ResultViewer data={result?.data} error={result?.error} rows={rows} columns={columns} title="Policy" />
      )}

      <PolicyModal
        visible={createOpen}
        header="Create policy"
        confirmText="Create"
        busy={create.busy}
        confirmDisabled={!canCreate}
        error={create.error}
        errorHeader="Create failed"
        onCancel={() => setCreateOpen(false)}
        onConfirm={confirmCreate}
      >
        <UserFields userType={userType} user={user} onUserTypeChange={setUserType} onUserChange={setUser} />
        <FormField label="Resource">
          <Select
            selectedOption={selectedResource}
            onChange={({ detail }) => setResourcePath(detail.selectedOption.value ?? '')}
            options={resourceOptions(resources.state.items)}
            {...optionsStatusProps(resources.state, 'resources')}
            placeholder="Choose a resource"
            ariaLabel="Resource"
          />
        </FormField>
        <FormField label="Actions">
          <Multiselect
            selectedOptions={actions}
            onChange={({ detail }) => setActions(detail.selectedOptions)}
            options={actionList.state.items}
            {...optionsStatusProps(actionList.state, 'actions')}
            placeholder="Choose actions"
            ariaLabel="Actions"
          />
        </FormField>
      </PolicyModal>

      <PolicyModal
        visible={del.pending !== null}
        header="Delete policy"
        confirmText="Delete"
        busy={del.busy}
        confirmDisabled={del.toRemove.length === 0}
        error={del.error}
        errorHeader="Delete failed"
        onCancel={del.close}
        onConfirm={del.confirm}
      >
        {del.pending && (
          <>
            <Box>
              Remove actions on <b>{del.pending.resourcePath}</b> from{' '}
              <b>{del.pending.userType} {del.pending.user}</b> in <b>{app}</b>?
            </Box>
            <ActionChecklist
              actions={del.pending.actions}
              selected={del.toRemove}
              onChange={del.setToRemove}
              disabled={del.busy}
            />
          </>
        )}
      </PolicyModal>
    </SpaceBetween>
  );
};
